'use client';

import React, { useEffect, useRef } from 'react';

export default function CorporateVerification() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el) => {
              el.classList.remove('hidden-init');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  const entityDetails = [
    { labelEs: 'Nombre Legal', labelEn: 'Legal Name', value: 'MERCALIA Connect Group LLC' },
    { labelEs: 'Jurisdicción', labelEn: 'Jurisdiction', value: 'New Mexico, USA' },
    { labelEs: 'Número de Registro', labelEn: 'Registration Number', value: '0008002716 *' },
    { labelEs: 'Representante Legal', labelEn: 'Managing Member', value: 'Jose S. Gardiol, Managing Member' },
    { labelEs: 'Informe BOI', labelEn: 'BOI Report', value: 'Presentado ante FinCEN (2026) · Filed with FinCEN (2026)' },
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-brand-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="text-center mb-16 reveal hidden-init">
          <span className="section-label block mb-4">Corporate Verification & Compliance</span>
          <h2
            className="font-display font-bold text-primary mb-4"
            style={{ fontSize: 'clamp(28px, 3.5vw, 48px)', letterSpacing: '-0.03em', lineHeight: '1.1' }}
          >
            Validación de Identidad Corporativa
            <br />
            <span className="italic font-light text-secondary">Corporate Identity Validation</span>
          </h2>
        </div>

        {/* Main Card */}
        <div
          className="relative rounded-3xl overflow-hidden reveal hidden-init"
          style={{ background: 'linear-gradient(145deg, #0B2C4A 0%, #0E3A60 60%, #0B2C4A 100%)', border: '1px solid rgba(42,155,173,0.2)' }}
        >
          {/* Background decorations */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full opacity-10"
              style={{ background: 'radial-gradient(circle, #2A9BAD 0%, transparent 70%)', transform: 'translate(20%, -20%)' }}
            />
            <div
              className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full opacity-8"
              style={{ background: 'radial-gradient(circle, #1A7A8A 0%, transparent 70%)', transform: 'translate(-20%, 20%)' }}
            />
            {/* Seal watermark */}
            <div className="absolute right-10 top-1/2 -translate-y-1/2 opacity-[0.04] hidden lg:block">
              <svg width="260" height="260" viewBox="0 0 24 24" fill="white">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
              </svg>
            </div>
          </div>

          <div className="relative z-10 p-10 md:p-14">
            {/* Bilingual Description */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Spanish */}
              <div
                className="rounded-2xl p-6"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(42,155,173,0.15)' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full" style={{ background: 'rgba(0,200,212,0.15)', color: '#00c8d4' }}>
                    Español
                  </span>
                </div>
                <h3 className="font-display font-semibold text-white text-lg mb-3">Una sociedad constituida en los Estados Unidos</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  MERCALIA Connect Group LLC es una sociedad constituida <strong className="font-semibold text-white/90">en</strong> los Estados Unidos, bajo jurisdicción de New Mexico y con número de registro 0008002716. En nuestras operaciones de importación, el importador de registro es una empresa estadounidense —no un exportador extranjero— y el permiso federal de NOAA Fisheries está emitido a nombre propio.
                </p>
              </div>

              {/* English */}
              <div
                className="rounded-2xl p-6"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(42,155,173,0.15)' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full" style={{ background: 'rgba(0,200,212,0.15)', color: '#00c8d4' }}>
                    English
                  </span>
                </div>
                <h3 className="font-display font-semibold text-white text-lg mb-3">A company incorporated in the United States</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  MERCALIA Connect Group LLC is a company incorporated <strong className="font-semibold text-white/90">in</strong> the United States, under New Mexico jurisdiction, registration number 0008002716. On our import operations the importer of record is a U.S. company — not a foreign exporter — and the federal NOAA Fisheries permit is issued in its own name.
                </p>
              </div>
            </div>

            {/* Entity Details Table */}
            <div className="mb-10">
              <h4 className="text-white/40 text-xs font-semibold tracking-widest uppercase mb-5">
                Datos de la Entidad / Entity Details
              </h4>
              <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(42,155,173,0.2)' }}>
                {entityDetails?.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-0 px-6 py-4 items-center"
                    style={{
                      borderBottom: index < entityDetails?.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                      background: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                    }}
                  >
                    <div className="flex flex-col md:flex-row md:gap-2">
                      <span className="text-white/40 text-xs">{item?.labelEs}</span>
                      <span className="text-white/25 text-xs hidden md:inline">/</span>
                      <span className="text-white/40 text-xs">{item?.labelEn}</span>
                    </div>
                    <div className="md:col-span-2 flex items-center gap-3">
                      {index === 4 ? (
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                            style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                            {item?.value}
                          </span>
                        </div>
                      ) : (
                        <span className="text-white font-medium text-sm">{item?.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notices Row */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Confidentiality Notice */}
              <div
                className="rounded-2xl p-5 flex gap-4"
                style={{ background: 'rgba(42,155,173,0.08)', border: '1px solid rgba(42,155,173,0.2)' }}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2A9BAD" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/50 text-xs font-semibold tracking-widest uppercase mb-2">Confidencialidad</p>
                  <p className="text-white/65 text-sm leading-relaxed">
                    La información validada mediante este código es de carácter privado y está destinada únicamente a las partes firmantes.
                  </p>
                </div>
              </div>

              {/* Security / FinCEN Compliance */}
              <div
                className="rounded-2xl p-5 flex gap-4"
                style={{ background: 'rgba(42,155,173,0.08)', border: '1px solid rgba(42,155,173,0.2)' }}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2A9BAD" strokeWidth="2">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/50 text-xs font-semibold tracking-widest uppercase mb-2">Seguridad / Security</p>
                  <p className="text-white/65 text-sm leading-relaxed">
                    Mercalia Connect Group LLC cumple con los estándares de reporte de transparencia del Departamento del Tesoro de los EE. UU. (FinCEN).
                  </p>
                </div>
              </div>
            </div>

            {/* Digital Signature Badge */}
            <div className="mt-8 pt-8 border-t border-white/10 flex flex-col items-stretch gap-5">
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2A9BAD" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" />
                </svg>
                <span className="text-white/60 text-xs">BOI filed with FinCEN · 2026</span>
              </div>

              {/* Verificacion de un tercero. OJO: el sello dice "verificada por ConnectAmericas",
                  NO por el BID. Se nombra al BID solo como la institucion detras de la plataforma. */}
              <a
                href="https://connectamericas.com/es/company/mercalia-connect-group-llc"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 p-4 rounded-lg w-full transition-colors"
                style={{ background: 'rgba(42,155,173,0.08)', border: '1px solid rgba(42,155,173,0.2)' }}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2A9BAD" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <p className="text-white/50 text-xs font-semibold tracking-widest uppercase mb-2">
                    Verificacion de tercero / Third-party verification
                  </p>
                  <p className="text-white/65 text-sm leading-relaxed">
                    <strong className="text-white/85">Empresa verificada por ConnectAmericas</strong>, la plataforma
                    de comercio del Banco Interamericano de Desarrollo (BID), con <strong className="text-white/85">2
                    certificaciones vigentes</strong>: permiso internacional de comercio pesquero y constitucion
                    societaria.
                    <span className="block mt-1 text-white/45">
                      Verified company on ConnectAmericas, the Inter-American Development Bank (IDB) trade platform,
                      with 2 certifications on file.
                    </span>
                  </p>
                </div>
              </a>

              {/* Verificador publico: cumple la promesa de "cada dato es verificable" */}
              <a
                href="/verify/"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors"
                style={{ background: 'rgba(0,200,212,0.12)', border: '1px solid rgba(0,200,212,0.35)', color: '#00c8d4' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                Verificar estas credenciales / Verify these credentials
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
